import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { useHandleActionItem } from "../../../hooks/useHandleActionItem.js";
import InputText from "../../../components/forms/InputText.jsx";
import InputDate from "../../../components/forms/InputDate.jsx";
import { apiUrl } from "../../../config/config.js";

const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
});

function AccountDetails() {
	const location = useLocation();
	const navigate = useNavigate();
	const locationType = location.state?.type || "";
	const data = useMemo(() => location.state?.data || [], [location.state?.data]);
	const [itemId, setItemId] = useState("");
	const [userId, setUserId] = useState("");
	const [roomId, setRoomId] = useState("");
	const [totalAmount, setTotalAmount] = useState("");
	const [checkIn, setCheckIn] = useState("");
	const [time, setTime] = useState("");
	const [people, setPeople] = useState("");
	const [notes, setNotes] = useState("");
	const [status, setStatus] = useState("");
	const [source, setSource] = useState("");
	const [checkOut, setCheckOut] = useState("");
	const [checkInTime, setCheckInTime] = useState("");
	const [checkOutTime, setCheckOutTime] = useState("");
	const [earlyCheckIn, setEarlyCheckIn] = useState(false);
	const [lateCheckOut, setLateCheckOut] = useState(false);
	const [reservationCode, setReservationCode] = useState("");
	const [selectedExtras, setSelectedExtras] = useState("");
	const [paymentChoice, setPaymentChoice] = useState("");
	const [paymentAddress, setPaymentAddress] = useState("");
	const [createdAt, setCreatedAt] = useState("");

	const { handleDelete, handleSubmit } = useHandleActionItem(data, "reservations");

	useEffect(() => {
		if (locationType === "edit" && data) {
			setItemId(data.ID || "");
			setUserId(data["ID Usuario"] || "");
			setRoomId(data["ID Habitación"] || "");
			setTotalAmount(data["Monto Total"] || "");
			setCheckIn(data["Check In"] || "");
			setTime(data.Tiempo || "");
			setPeople(data.Personas || "");
			setNotes(data.Notas || "");
			setStatus(data.Estado || "");
			setSource(data.Fuente || "");
			setCheckOut(data["Check Out"] || "");
			setCheckInTime(data["Hora Entrada"] || "");
			setCheckOutTime(data["Hora Salida"] || "");
			setEarlyCheckIn(data["Entrada Temprana"] === "1");
			setLateCheckOut(data["Salida Tardía"] === "1");
			setReservationCode(data["Código Reserva"] || "");
			setSelectedExtras(data["Extras Seleccionados"] || "");
			setPaymentChoice(data["Método de Pago"] || "");
			setPaymentAddress(data["Dirección de Pago"] || "");
			setCreatedAt(data.Creado || "");
		}
	}, [locationType, data]);

	const validateForm = () => {
		if (!userId || !roomId || !totalAmount || !checkIn || !time || !people || !status || !checkInTime || !checkOutTime) {
			Toast.fire({
				icon: "error",
				title: "Todos los campos requeridos deben ser completados",
				background: "#1e2836",
			});
			return false;
		}
		return true;
	};

	const handleDeleteForm = async (event, itemId) => {
		event.preventDefault();
		if (!validateForm()) return;
		const data = {
			id: itemId,
		};
		await handleDelete(data, "reservations");
	};

	const handleSubmitForm = async (event) => {
		event.preventDefault();
		if (!validateForm()) return;
		await handleSubmit(locationType === "add" ? "add" : "update", "reservations", {
			id: itemId,
			userId,
			roomId,
			totalAmount,
			checkIn,
			time,
			people,
			notes,
			status,
			source,
			checkOut,
			checkInTime,
			checkOutTime,
			earlyCheckIn: earlyCheckIn ? "1" : "0",
			lateCheckOut: lateCheckOut ? "1" : "0",
			reservationCode,
			selectedExtras,
			paymentChoice,
			paymentAddress,
			createdAt,
		});
		navigate("/reservations");
	};

	return (
		<div>
			<form onSubmit={handleSubmitForm}>
				<h1 className="text-center">{locationType === "add" ? "Agregar Reservación" : "Actualizar Reservación"}</h1>

				<InputText label="ID Usuario" type="number" value={userId} setValue={setUserId} />
				<InputText label="ID Habitación" type="number" value={roomId} setValue={setRoomId} />
				<InputText label="Monto Total" type="number" value={totalAmount} setValue={setTotalAmount} />
				<InputText label="Tiempo" value={time} setValue={setTime} />
				<InputText label="Personas" type="number" value={people} setValue={setPeople} />
				<InputText label="Notas" value={notes} setValue={setNotes} />

				<div className="mb-3">
					<label className="form-label">
						Estado <span className="text-danger">*</span>
					</label>
					<select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
						<option value="pending">Pendiente</option>
						<option value="paid">Pagado</option>
					</select>
				</div>

				<InputText label="Fuente" value={source} setValue={setSource} />
				<InputDate label="Check In" value={checkIn} setValue={setCheckIn} />
				<InputDate label="Check Out" value={checkOut} setValue={setCheckOut} />
				<InputText label="Hora Entrada" type="time" value={checkInTime} setValue={setCheckInTime} />
				<InputText label="Hora Salida" type="time" value={checkOutTime} setValue={setCheckOutTime} />

				<div className="mb-3">
					<label className="form-label">Entrada Temprana</label>
					<div className="form-check form-switch">
						<input
							className="form-check-input"
							type="checkbox"
							checked={earlyCheckIn}
							onChange={(e) => setEarlyCheckIn(e.target.checked)}
						/>
					</div>
				</div>

				<div className="mb-3">
					<label className="form-label">Salida Tardía</label>
					<div className="form-check form-switch">
						<input
							className="form-check-input"
							type="checkbox"
							checked={lateCheckOut}
							onChange={(e) => setLateCheckOut(e.target.checked)}
						/>
					</div>
				</div>

				<InputText label="Código Reserva" value={reservationCode} setValue={setReservationCode} />
				<InputText label="Extras Seleccionados" value={selectedExtras} setValue={setSelectedExtras} />
				<InputText label="Método de Pago" value={paymentChoice} setValue={setPaymentChoice} />
				<InputText label="Dirección de Pago" value={paymentAddress} setValue={setPaymentAddress} />
				<InputDate label="Creado" value={createdAt} setValue={setCreatedAt} />

				<div className="mb-3 d-flex justify-content-around">
					<button type="submit" className="btn btn-outline-theme btn-lg">
						{locationType === "add" ? "Agregar" : "Actualizar"}
					</button>
					<button onClick={() => navigate("/reservations")} className="btn btn-outline-theme btn-lg">
						Atrás
					</button>
					<button onClick={(event) => handleDeleteForm(event, itemId)} className="btn btn-outline-danger btn-lg">
						Eliminar
					</button>
				</div>
			</form>
		</div>
	);
}

export default AccountDetails;
